<template>
  <div class="d-flex flex-column">
    <StandarPageWithList
      title="All Users"
      :activeNo="$store.getters.activeUserCount"
      :empty="showOrganisationEmptyState"
      :showIcon="isListLoading ? false : true"
    >
      <template #action><span></span></template>
      <template #empty-content>
        <div v-if="!isListLoading" class="d-flex flex-column align-center">
          <h3 class="mb-2">No users found</h3>
          <p class="text-center mb-8">
            You need some users in your <br />
            organization
          </p>
          <Button
            :plusIcon="'add-icon.svg'"
            :general="true"
            :label="'Add System Admin'"
            @onClick="addUser('add')"
            class="empty-btn"
          ></Button>
        </div>
      </template>
      <div class="px-5 mb-5">
        <SearchBar
          class="admin-user-searchbar"
          @onSearch="onFilter"
          type="button"
          :label="appliedFilters.length > 0 ? 'More Filters' : 'Filters'"
          :length="appliedFilters.length"
          :chipText="appliedFilters"
          ghostText="Search"
          :add="true"
          btn-text="Add System Admin"
          @onClick="addUser"
          :valueSearch="filterText"
          @removeFilter="removeFilter"
        ></SearchBar>
      </div>
      <SharedTableSkeleton
        v-if="showSkeletonLoading"
        :row-count="paginationModel.perPage || 5"
        :column-count="5"
        column-template="2fr 1.5fr 1fr 1fr 60px"
      />
      <DataTable
        v-else-if="filteredUsers.length > 0"
        :pagination="paginationModel"
        :headers="headers"
        :data="filteredUsers"
        @onRequest="requestData"
      >
        <template #no-data> </template>
        <template #user="{ row }">
          <span class="admin-name" @click="gotoAccounts(row)">
            <UserAdminCard
              class="my-4"
              :magentaHoverEnable="true"
              :fullName="row.user.data.first_name + ' ' + row.user.data.last_name"
              :email="row.user.data.email"
            ></UserAdminCard>
          </span>
        </template>
        <template #account_access="{ row }">
          <div>
            <ExUserAccessBadge
              v-if="
                row.user.data.account_permissions && row.user.data.account_permissions.length > 0
              "
              :account_name="row.user.data.account_permissions[0].account_name"
              :permission="getPermissionType(row.user.data.account_permissions[0])"
              :access="getAccessType(row.user.data.account_permissions[0])"
              account_status="Open"
              :accounts_count="row.user.data.account_permissions.length - 1"
            />
          </div>
        </template>
        <template #lastLogin="{ row }">
          <div class="last-login-text">
            {{ row?.user?.data?.last_login || "" | formatDate }}
          </div>
        </template>
        <template #status="{ row }">
          <UserAdminStatusBadge
            :status="row.user.data.state[0].toUpperCase() + row.user.data.state.substring(1)"
          />
        </template>
        <template #actions="{ row }">
          <div>
            <DropDownMenu
              @onClick="triggerDropdownAction(row.user.data, $event)"
              :is-open="false"
              :dropdown-items="getAdminUsersItems(row.user.data, appUser)"
              scrollContainerClass=".ex-main-container"
              attachSelector=".ex-main-container"
              :absolute="true"
            >
              <template #activator>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#344054"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                    stroke="#344054"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                    stroke="#344054"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
            </DropDownMenu>
          </div>
        </template>
      </DataTable>
    </StandarPageWithList>
    <ExActionsPopUp
      v-if="userAccountActionDialogObject?.show"
      :headline="userAccountActionDialogObject?.title"
      :htmlInfo="userAccountActionDialogObject.description"
      :actionLabel="userAccountActionDialogObject?.actionText"
      :value="userAccountActionDialogObject?.show"
      :primary="userAccountActionDialogObject.primary"
      :termination="userAccountActionDialogObject.termination"
      :destructive="userAccountActionDialogObject.destructive ?? false"
      :avatar="{
        picture: '',
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
      }"
      @onAction="OnActionUserAccountDialog"
      @input="OnCloseUserAccountDialog"
    />
    <DeleteAdminUserPopup
      :show="userActions.showDeleteUserPopup"
      :user="{
        id: currentUser?.id || 0,
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
      }"
      @onDeleteAdminUser="deleteAdminUser"
      @onClose="onCloseActionPopup"
    />
    <ExCreateUserWizard
      :dialog="addUserDialog"
      :accessLevel="'owner'"
      @created="onCreatedUser"
      :isSuperUser="isSuperUser"
      @dialog-closed="addUserDialog = false"
      :allUsers="wizardUserDirectory"
    />
    <RenameAdminUserPopup
      v-model="userActions.showRenameUserPopup"
      @input="onCloseActionPopup"
      @onSave="renameAdminUser"
      :current-user="currentUser"
      :existingNames="filteredUsers.map((el) => el.user.data.first_name)"
    />
    <CancelInviteAdminUserPopup
      :show="userActions.showCancelInviteUserPopup"
      :user="{
        id: currentUser?.id || 0,
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
      }"
      @onCancelInviteAdminUser="cancelInviteAdminUser"
      @onClose="onCloseActionPopup"
    />
    <SearchModal
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      @checkedItemsChanged="filteredModalUsers"
      :clearSelection="clearSelection"
      filterType="Users"
      :filterGroups="filterGroups"
      :statusOptions="statusOptions"
      :modalWidth="620"
      :defaultSelected="appliedFilters"
    />
    <ResendInviteAdminUserPopup
      :show="userActions.showResendInviteUserPopup"
      :user="{
        id: currentUser?.id || 0,
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
      }"
      @onResendInviteAdminUser="resendInviteAdminUser"
      @onClose="onCloseActionPopup"
    />
    <NoRecordFound
      v-if="showNoMatchingUsersPanel"
      :title="title"
      :message="title"
      :btnTitle="'System Admin'"
      :subtitle="''"
      @onAction="addUser('add')"
      class="no-record-found account-not-found"
      :clearLabel="clearBtnLabel"
      :showClearAction="true"
      @onClear="clearSearchFilter()"
      :description="emptyRecordDescription"
    />
    <ExAdminUserAccountSelector
      v-if="showResendInvite"
      v-model="showResendInvite"
      :accounts="sanitizedAccounts"
      @onClose="showResendInvite = false"
      @selectAccount="resendInviteAdminUser"
      :user="{
        id: currentUser?.id || 0,
        firstName: currentUser?.first_name || '',
        lastName: currentUser?.last_name || '',
      }"
    />
  </div>
</template>
<script>
import StandarPageWithList from "@components/common/PageHeaderWithButton.vue";
import DataTable from "@components/DataTable/DataTable.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import UserAdminCard from "@components/common/UserAdminCard.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import UserAdminStatusBadge from "@components/UserAdminStatusBadge/UserAdminStatusBadge.vue";
import ExUserAccessBadge from "@components/UserAccessBadge/ExUserAccessBadge.vue";
import Button from "@components/buttons/Button.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import DeleteAdminUserPopup from "@components/AdminUserActions/DeleteAdminUserPopup.vue";
import RenameAdminUserPopup from "@components/AdminUserActions/RenameAdminUserPopup.vue";
import ExAdminUserAccountSelector from "@components/AdminSelectAccountResendInvite/ExAdminUserAccountSelector.vue";
import {
  getSystemUsers,
  deleteAdminUser,
  renameAdminUser,
  cancelInviteUser,
  blockAdminUser,
  unBlockAdminUser,
  closeUser,
  unCloseUser,
  resendInviteAdminUser,
} from "@/api";
import CancelInviteAdminUserPopup from "@components/AdminUserActions/CancelInviteAdminUserPopup.vue";
import ResendInviteAdminUserPopup from "@components/AdminUserActions/ResendInviteAdminUserPopup.vue";
import ExCreateUserWizard from "@components/ExCreateUserWizard.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import { createNamespacedHelpers } from "vuex";
const { mapActions } = createNamespacedHelpers("isLoading");
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import clearStateMixin from "@components/views/mixins/clearStateMixin.js";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import { getFilters, updateFilters } from "@components/helper/localStorageHelper";

export default {
  name: "AdminDataTable",
  components: {
    SearchBar,
    DataTable,
    StandarPageWithList,
    UserAdminCard,
    DropDownMenu,
    ExUserAccessBadge,
    UserAdminStatusBadge,
    Button,
    DeleteAdminUserPopup,
    ExCreateUserWizard,
    RenameAdminUserPopup,
    CancelInviteAdminUserPopup,
    ExActionsPopUp,
    SearchModal,
    ResendInviteAdminUserPopup,
    NoRecordFound,
    ExAdminUserAccountSelector,
    SharedTableSkeleton,
  },
  mixins: [dropdownMixin, clearStateMixin],
  props: {
    headers: {
      type: Array,
      default() {
        return [
          {
            text: "User",
            sortable: false,
            value: "user",
            img: "icons/accounts.svg",
          },
          {
            text: "Access to Account",
            sortable: false,
            value: "accounts_access",
          },
          {
            text: "Last seen in account",
            sortable: false,
            value: "last_login",
          },
          {
            text: "Status",
            sortable: false,
            value: "status",
          },
          {
            text: "",
            sortable: false,
            value: "actions",
          },
        ];
      },
    },
    pagination: {
      type: Object,
      default() {
        return {
          page: 1,
          total: 0,
          perPage: 10,
        };
      },
    },
  },
  beforeMount() {
    this.appliedFilters = this.getStoredFilters();
  },
  async mounted() {
    await this.checkSkeletonFlag();
    if (!this.skeletonLoadingEnabled) {
      this.$loader.show();
    }
    await this.loadUsers();
    if (!this.skeletonLoadingEnabled) {
      this.$loader.hide();
    }
    const user = JSON.parse(localStorage.getItem("user"));
    this.isSuperUser = user.is_superuser;
  },
  data() {
    return {
      filterText: "",
      showFilterDialog: false,
      appliedFilters: [],
      modalDialog: false,
      paginationModel: this.pagination,
      accounts: this.data,
      systemUsers: [],
      currentUser: null,
      loading: false,
      skeletonLoadingEnabled: false,
      userActions: {
        showDeleteUserPopup: false,
        showRenameUserPopup: false,
        showCancelInviteUserPopup: false,
        showBlockUserPopup: false,
        showUnBlockUserPopup: false,
        showCloseUserPopup: false,
        showUnCloseUserPopup: false,
        showResendInviteUserPopup: false,
      },
      addUserDialog: false,
      clearSelection: "",
      statusOptions: ["invited", "operational", "closed", "blocked"],
      filterGroups: [],
      isSuperUser: false,
      title: "Users",
      showResendInvite: false,
      usersSearchQuery: "",
      usersSearchDebounceTimer: null,
      userListHasHadRows: false,
      wizardUserDirectory: [],
    };
  },
  beforeDestroy() {
    if (this.usersSearchDebounceTimer) {
      clearTimeout(this.usersSearchDebounceTimer);
    }
  },
  computed: {
    showSkeletonLoading() {
      return this.loading && this.skeletonLoadingEnabled;
    },
    isListLoading() {
      return this.$store.state.isLoading.isLoading || this.showSkeletonLoading;
    },
    hasActiveSearchQuery() {
      return !!(this.filterText && String(this.filterText).trim());
    },
    showOrganisationEmptyState() {
      if (this.showSkeletonLoading) return false;
      const noRows = !this.systemUsers.length;
      if (!noRows) return false;
      if (this.hasActiveSearchQuery) return false;
      if (this.userListHasHadRows) return false;
      return true;
    },
    showNoMatchingUsersPanel() {
      if (this.showSkeletonLoading) return false;
      if (this.filteredUsers && this.filteredUsers.length > 0) return false;
      if (this.systemUsers && this.systemUsers.length > 0) return true;
      if (this.hasActiveSearchQuery) return true;
      if (this.userListHasHadRows) return true;
      return false;
    },
    sanitizedAccounts() {
      if (
        this.currentUser &&
        this.currentUser?.account_permissions &&
        this.currentUser?.account_permissions.length > 0
      ) {
        return this.currentUser?.account_permissions.map((el) => {
          const newEl = {};
          newEl.id = el.account;
          newEl.name = el.account_name;
          return newEl;
        });
      }
      return [];
    },
    appUser() {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      return currentUser;
    },
    userAccountActionDialogObject() {
      if (this.userActions.showBlockUserPopup) {
        const firstName = this.currentUser?.first_name || "";
        const lastName = this.currentUser?.last_name || "";
        return {
          title: "Block",
          description: `You are about to block ${firstName} ${lastName}`,
          actionText: "Block",
          show: true,
          primary: false,
          termination: true,
        };
      } else if (this.userActions.showUnBlockUserPopup) {
        const firstName = this.currentUser?.first_name || "";
        const lastName = this.currentUser?.last_name || "";
        return {
          title: "Un-block",
          description: `You are about to un-block ${firstName} ${lastName}`,
          actionText: "Un-block",
          show: true,
          primary: true,
          termination: false,
        };
      } else if (this.userActions.showCloseUserPopup) {
        const firstName = this.currentUser?.first_name || "";
        const lastName = this.currentUser?.last_name || "";
        return {
          title: "Close",
          description: `You are about to close ${firstName} ${lastName}`,
          actionText: "Close",
          show: true,
          primary: false,
          termination: false,
          destructive: true,
        };
      } else if (this.userActions.showUnCloseUserPopup) {
        const firstName = this.currentUser?.first_name || "";
        const lastName = this.currentUser?.last_name || "";
        return {
          title: "Un-close",
          description: `You are about to un-close ${firstName} ${lastName}`,
          actionText: "Un-close",
          show: true,
          primary: true,
          termination: false,
        };
      }
      return {
        title: "",
        description: "",
        actionText: "",
        show: false,
        primary: false,
        termination: false,
      };
    },
    filteredUsers() {
      const order = {
        invited: 1,
        operational: 2,
        suspended: 3,
        blocked: 4,
        closed: 5,
      };
      const maxOrder = 99;

      const resultArray = this.systemUsers.map((el) => {
        return {
          user: {
            slot: "user",
            data: el,
          },
          accountAccess: {
            slot: "account_access",
          },
          lastLogin: {
            slot: "lastLogin",
          },
          status: {
            slot: "status",
          },
          actions: {
            slot: "actions",
          },
        };
      });

      resultArray.sort((a, b) => {
        const aOrder = order[a.user.data.state] || maxOrder;
        const bOrder = order[b.user.data.state] || maxOrder;
        return aOrder - bOrder;
      });

      return resultArray;
    },
  },
  watch: {
    appliedFilters(newFilters, oldFilters) {
      this.storeFilters(newFilters);
      if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
        this.paginationModel.page = 1;
      }
    },
  },
  methods: {
    getStoredFilters() {
      const filters = getFilters("userAppliedFilter");
      if (Array.isArray(filters) && filters.length > 0) {
        return filters;
      }
      return getDefaultFiltersForKey("userAppliedFilter") || [];
    },
    storeFilters(filters) {
      updateFilters("userAppliedFilter", filters);
    },
    filteredModalUsers(filters) {
      const newArray = filters
        .map((item) => {
          if (item.value !== null) {
            if (typeof item === "string") {
              return item;
            } else if (
              typeof item === "object" &&
              // eslint-disable-next-line no-prototype-builtins
              item.hasOwnProperty("value")
            ) {
              return item.value;
            }
          }
        })
        .filter(Boolean);
      this.appliedFilters = newArray;
      this.paginationModel.page = 1;
      this.$nextTick(async () => {
        this.showFilterDialog = false;
        await this.loadUsers();
      });
    },
    removeFilter(removedFilter) {
      this.clearSelection = removedFilter;
      this.paginationModel.page = 1;
      this.$nextTick(() => {
        this.loadUsers();
      });
    },

    ...mapActions(["setLoadingStatus"]),
    async loadWizardUserDirectory() {
      try {
        const { data } = await getSystemUsers({
          page: 1,
          size: 500,
          limit: 500,
          offset: 0,
        });
        this.wizardUserDirectory = data.results || [];
      } catch (e) {
        console.error("Failed to load users for wizard:", e);
        this.wizardUserDirectory = [];
      }
    },
    async addUser(type) {
      if (type === "add") {
        await this.loadWizardUserDirectory();
        this.addUserDialog = true;
      } else if (type === "filter") {
        this.showFilterDialog = true;
      }
    },
    async onCreatedUser() {
      await this.loadUsers();
    },
    async checkSkeletonFlag() {
      try {
        this.skeletonLoadingEnabled = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
      } catch (error) {
        this.skeletonLoadingEnabled = false;
      }
    },
    async loadUsers() {
      this.loading = true;
      try {
        const perPage = this.paginationModel.perPage;
        const page = this.paginationModel.page;
        const limit = perPage;
        const offset = Math.max(0, (page - 1) * perPage);
        const statusList = this.appliedFilters.length > 0 ? [...this.appliedFilters] : undefined;
        const search =
          this.usersSearchQuery && this.usersSearchQuery.length > 0
            ? this.usersSearchQuery
            : undefined;

        const listParams = {
          page,
          size: limit,
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(statusList ? { status: statusList } : {}),
        };

        const [listRes, operationalRes] = await Promise.all([
          getSystemUsers(listParams),
          getSystemUsers({
            page: 1,
            size: 1,
            limit: 1,
            offset: 0,
            status: "operational",
          }),
        ]);

        const { data } = listRes;
        this.paginationModel.total = Number(data.count) || 0;
        if (this.paginationModel.total > 0) {
          this.userListHasHadRows = true;
        }
        this.systemUsers = data.results || [];
        this.$store.commit("setActiveUserCount", Number(operationalRes.data.count) || 0);
      } catch (error) {
        console.error("Failed to load users:", error);
        this.systemUsers = [];
        this.paginationModel.total = 0;
        this.$store.commit("setActiveUserCount", 0);
      } finally {
        this.loading = false;
      }
    },
    onCloseActionPopup() {
      if (this.$loader.isAppLoading) {
        const dialogEl = document.querySelector(".v-dialog.v-dialog--active");
        if (dialogEl) {
          dialogEl.classList.remove("shake-animation");
          void dialogEl.offsetWidth;
          dialogEl.classList.add("shake-animation");

          setTimeout(() => {
            dialogEl.classList.remove("shake-animation");
          }, 400);
        }
        return;
      }

      this.currentUser = null;
      this.userActions.showDeleteUserPopup = false;
      this.userActions.showRenameUserPopup = false;
      this.userActions.showCancelInviteUserPopup = false;
      this.userActions.showBlockUserPopup = false;
      this.userActions.showUnBlockUserPopup = false;
      this.userActions.showCloseUserPopup = false;
      this.userActions.showUnCloseUserPopup = false;
      this.userActions.showResendInviteUserPopup = false;
      this.showResendInvite = false;
    },
    async deleteAdminUser() {
      this.$loader.setAppLoading(true);
      await deleteAdminUser(this.currentUser.id);
      await this.loadUsers();
      this.$loader.setAppLoading(false);
      this.onCloseActionPopup();
    },
    OnActionUserAccountDialog() {
      if (this.userActions.showBlockUserPopup) {
        this.blockAdminUser();
      } else if (this.userActions.showUnBlockUserPopup) {
        this.unBlockAdminUser();
      } else if (this.userActions.showCloseUserPopup) {
        this.closeAdminUser();
      } else if (this.userActions.showUnCloseUserPopup) {
        this.unCloseAdminUser();
      }
    },
    OnCloseUserAccountDialog() {
      if (this.userActions.showBlockUserPopup) {
        this.userActions.showBlockUserPopup = false;
      } else if (this.userActions.showUnBlockUserPopup) {
        this.userActions.showUnBlockUserPopup = false;
      } else if (this.userActions.showCloseUserPopup) {
        this.userActions.showCloseUserPopup = false;
      } else if (this.userActions.showUnCloseUserPopup) {
        this.userActions.showUnCloseUserPopup = false;
      }
    },
    async blockAdminUser() {
      this.setLoadingStatus(true);
      if (this.currentUser && this.currentUser.id) {
        try {
          await blockAdminUser(this.currentUser.id);
          await this.loadUsers();
        } catch (e) {
          console.log("error: ", e);
        }
      }
      this.setLoadingStatus(false);
      this.OnCloseUserAccountDialog();
    },
    async closeAdminUser() {
      this.setLoadingStatus(true);
      if (this.currentUser && this.currentUser.id) {
        try {
          await closeUser(this.currentUser.id);
          await this.loadUsers();
        } catch (e) {
          console.log("error: ", e);
        }
      }
      this.setLoadingStatus(false);
      this.OnCloseUserAccountDialog();
    },
    async unCloseAdminUser() {
      this.setLoadingStatus(true);
      if (this.currentUser && this.currentUser.id) {
        try {
          await unCloseUser(this.currentUser.id);
          await this.loadUsers();
        } catch (e) {
          console.log("error: ", e);
        }
      }
      this.setLoadingStatus(false);
      this.OnCloseUserAccountDialog();
    },
    async unBlockAdminUser() {
      this.setLoadingStatus(true);
      if (this.currentUser && this.currentUser.id) {
        try {
          await unBlockAdminUser(this.currentUser.id);
          await this.loadUsers();
        } catch (e) {
          console.log("error: ", e);
        }
      }
      this.setLoadingStatus(false);
      this.OnCloseUserAccountDialog();
    },
    getAccessType(account) {
      if (account) {
        if (account.has_access) {
          return "Has Access";
        } else if (account.is_suspended) {
          return "Suspended";
        } else {
          return "Blocked";
        }
      } else {
        return "Blocked";
      }
    },
    getPermissionType(account) {
      if (account) {
        if (account.is_owner) {
          return "Owner";
        } else if (account.is_co_owner) {
          return "Co-Owner";
        } else if (
          account.has_readiness_access ||
          account.has_knowledge_access ||
          account.has_performance_access ||
          account.has_compensation_access
        ) {
          return "App User";
        }
      } else {
        return null;
      }
    },
    onFilter(data) {
      this.filterText = data;
      if (this.usersSearchDebounceTimer) {
        clearTimeout(this.usersSearchDebounceTimer);
      }
      const trimmed = (data || "").trim();
      if (trimmed === "") {
        this.usersSearchQuery = "";
        this.paginationModel.page = 1;
        this.loadUsers();
        return;
      }
      this.usersSearchDebounceTimer = setTimeout(() => {
        this.usersSearchQuery = trimmed;
        this.paginationModel.page = 1;
        this.loadUsers();
      }, 350);
    },
    triggerDropdownAction(data, event) {
      this.currentUser = data;
      this.setLoadingStatus(false);
      switch (event.value) {
        case "delete_action":
          this.userActions.showDeleteUserPopup = true;
          break;
        case "block_action":
          this.userActions.showBlockUserPopup = true;
          break;
        case "unblock_action":
          this.userActions.showUnBlockUserPopup = true;
          break;
        case "close_action":
          this.userActions.showCloseUserPopup = true;
          break;
        case "unclose_action":
          this.userActions.showUnCloseUserPopup = true;
          break;
        case "rename_action":
          this.userActions.showRenameUserPopup = true;
          break;
        case "cancel_invite_action":
          this.userActions.showCancelInviteUserPopup = true;
          break;
        case "resent_invite_action":
          if (this.currentUser?.account_permissions.length > 1) {
            this.showResendInvite = true;
          } else {
            this.userActions.showResendInviteUserPopup = true;
          }
          break;
        default:
          break;
      }
    },
    async requestData(data) {
      this.paginationModel.page = data.page;
      await this.loadUsers();
    },
    async loadAccounts(pageNo = 1) {
      if (this.data) {
        const startIndex = (pageNo - 1) * this.paginationModel.perPage;
        const endIndex = startIndex + this.paginationModel.perPage;
        this.accounts = this.data.slice(startIndex, endIndex);
        this.paginationModel.total = this.data.length;
      }
    },
    gotoAccounts(data) {
      const firstPerm = data.user.data.account_permissions?.[0];
      if (!firstPerm || firstPerm.account == null) {
        this.$notifications?.error("No account is linked to this user.");
        return;
      }
      const UserInfo = {
        account: `${data.user.data.first_name} ${data.user.data.last_name}`,
        email: data.user.data.email,
        status: data.user.data.state,
        id: data.user.data.id,
        userName: `${data.user.data.first_name} ${data.user.data.last_name}`,
        last_login: data.user.data.last_login ?? null,
        account_permissions: data.user.data.account_permissions || [],
      };
      localStorage.setItem("selectedAccount", JSON.stringify(UserInfo));
      this.$router.push({
        name: "UserAccountAccessView",
        params: {
          accountId: String(firstPerm.account),
          userId: String(data.user.data.id),
        },
      });
    },
    async renameAdminUser(newUser) {
      if (this.currentUser && this.currentUser.id) {
        this.$loader.setAppLoading(true);
        try {
          const payload = {
            first_name: newUser.firstName,
            last_name: newUser.lastName,
          };
          let res = await renameAdminUser(this.currentUser.id, payload);
          await this.loadUsers();

          // Update localStorage if the renamed user is the current user
          const currentUser = JSON.parse(localStorage.getItem("user"));
          if (currentUser && currentUser.id === res?.data?.id) {
            currentUser.first_name = newUser.firstName;
            currentUser.last_name = newUser.lastName;
            localStorage.setItem("user", JSON.stringify(currentUser));
            this.$store.dispatch("updateUserData", currentUser);
          }
        } catch (e) {
          console.log("error: ", e);
        }
        this.$loader.setAppLoading(false);
        this.userActions.showRenameUserPopup = false;
      }
    },
    async cancelInviteAdminUser() {
      this.$loader.setAppLoading(true);
      const userId = this.currentUser?.id;
      try {
        await cancelInviteUser(userId);
        await this.loadUsers();
      } catch (e) {
        console.error("error: ", e);
      }
      this.$loader.setAppLoading(false);
      this.onCloseActionPopup();
    },
    async resendInviteAdminUser(account) {
      const adminUser = JSON.parse(localStorage.getItem("user"));
      this.$loader.setAppLoading(true);
      try {
        let inviteAccount = null;
        if (account && account.id) {
          const tempAccount = this.currentUser.account_permissions.find(
            (el) => el.account == account.id
          );
          inviteAccount = tempAccount.account;
        } else {
          inviteAccount = this.currentUser.account_permissions[0].account;
        }
        const payload = {
          user: this.currentUser.id,
          account: inviteAccount,
        };
        await resendInviteAdminUser(adminUser.id, payload);
        await this.loadUsers();
      } catch (e) {
        console.error("error: ", e);
      }
      this.$loader.setAppLoading(false);
      this.onCloseActionPopup();
    },
    async clearSearchFilter() {
      const hadSearch = this.hasActiveSearchQuery;
      const hadFilters = Array.isArray(this.appliedFilters) && this.appliedFilters.length > 0;

      if (hadSearch && hadFilters) {
        this.appliedFilters = [];
        this.filterText = "";
        this.usersSearchQuery = "";
        this.paginationModel.page = 1;
        await this.loadUsers();
        return;
      }
      if (hadFilters) {
        this.appliedFilters = [];
        this.paginationModel.page = 1;
        await this.loadUsers();
      }
      if (hadSearch) {
        this.filterText = "";
        this.usersSearchQuery = "";
        this.paginationModel.page = 1;
        await this.loadUsers();
      }
    },
  },
  filters: {
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);

      const day = date.getUTCDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      const year = date.getUTCFullYear();

      const hours = date.getUTCHours().toString().padStart(2, "0");
      const minutes = date.getUTCMinutes().toString().padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },
  },
};
</script>
<style>
.admin-user-searchbar button,
.admin-empty-content-user-add {
  font-size: 14px !important;
}

.admin-empty-content-user-add {
  width: 170px;
}

.admin-name {
  cursor: pointer;
}

.shake-animation {
  animation: pop 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) !important;
}

@keyframes pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}
</style>
<style scoped>
.last-login-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  color: #667085;
}
.empty-btn {
  color: white;
}
::v-deep(.account-name) {
  padding: 15px 20px !important;
}
::v-deep(.account-name):hover {
  padding: 15px 20px !important;
  background-color: #f2edff;
  box-shadow: 0 4px 4px 0 hsla(0, 0%, 40%, 0.1);
  border-radius: 8px;
  cursor: url("../assets/cursorArrow.svg"), auto !important;
}
</style>
